import { sectionRenderer } from "@theme/section-renderer";
import { Component } from "@theme/component";
import { FilterUpdateEvent, ThemeEvents } from "@theme/events";
import { debounce, formatMoney, showLoader, hideLoader, showDrawerLoader, hideDrawerLoader } from "@theme/utilities";

/**
 * Escapes a value for HTML text or a quoted attribute (C-17).
 * @param {unknown} value
 * @returns {string}
 */
const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");


/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = "q";

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ["facetsForm"];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get("filter.v.price.gte") === "")
      newParameters.delete("filter.v.price.gte");
    if (newParameters.get("filter.v.price.lte") === "")
      newParameters.delete("filter.v.price.lte");

    newParameters.delete("page");

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    // Keep alternate template views (e.g. ?view=quince-plp) across filter/sort updates
    const viewParam = new URL(window.location.href).searchParams.get("view");
    if (viewParam) newParameters.set("view", viewParam);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? "";
  }

  get sectionId() {
    const id = this.getAttribute("section-id");
    if (!id) throw new Error("Section ID is required");
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = "";
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState(
      { urlParameters: urlParameters.toString() },
      "",
      url.toString()
    );
  }

  /**
   * Cleans up search-related modifications from filter items
   * Removes data-filter-hidden attributes and restores display styles
   */
  #cleanupSearchModifications() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    // Find all filter list items with data-filter-hidden
    const hiddenItems = section.querySelectorAll('li[data-filter-hidden="true"]');
    hiddenItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      // Remove data-filter-hidden attribute
      item.removeAttribute('data-filter-hidden');
      // Restore display style
      item.style.display = '';
    });
  }

  /**
   * Saves search input values before filter update
   */
  #saveSearchValues() {
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const searchValues = new Map();
    const searchInputs = section.querySelectorAll('.facets__search-input');
    
    searchInputs.forEach((input) => {
      if (input instanceof HTMLInputElement && input.value.trim() !== '') {
        const inputId = input.id;
        searchValues.set(inputId, input.value);
      }
    });
    
    return searchValues.size > 0 ? searchValues : null;
  }

  /**
   * Saves expanded states of show-more components before filter update
   */
  #saveShowMoreStates() {
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const expandedStates = new Map();
    const showMoreComponents = section.querySelectorAll('show-more-component');
    
    showMoreComponents.forEach((component) => {
      // Find the filter section to get a unique identifier
      const filterSection = component.closest('.facets__filter, .facets-drawer__filter-content');
      if (!filterSection) return;
      
      const filterParam = filterSection.getAttribute('data-filter-param') || 
                          filterSection.querySelector('[data-filter-param]')?.getAttribute('data-filter-param');
      if (!filterParam) return;
      
      const isExpanded = component.getAttribute('data-expanded') === 'true';
      if (isExpanded) {
        expandedStates.set(filterParam, true);
      }
    });
    
    return expandedStates.size > 0 ? expandedStates : null;
  }

  /**
   * Checks if the filter drawer is currently open
   * @returns {boolean} True if the filter drawer is open, false otherwise
   */
  #isFilterDrawerOpen() {
    const filterDrawer = document.getElementById('filters-drawer');
    if (!filterDrawer) return false;
    
    const dialog = filterDrawer.querySelector('dialog');
    return !!(dialog && dialog.hasAttribute('open'));
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    const isMobile = window.innerWidth < 750;
    const isFilterDrawerOpen = this.#isFilterDrawerOpen();
    
    // Show drawer loader only if filter drawer is open on mobile
    // Otherwise use regular loader
    const loaderElement = (isMobile && isFilterDrawerOpen) ? showDrawerLoader() : showLoader();
    const isDrawerLoader = isMobile && isFilterDrawerOpen;
    
    // Save search values to global before updating filters (will be restored in section-renderer.js after morph)
    const savedSearchValues = this.#saveSearchValues();
    if (savedSearchValues && savedSearchValues.size > 0) {
      window._savedFilterSearchValues = savedSearchValues;
    } else {
      // Clear any previously saved values when search is empty
      window._savedFilterSearchValues = null;
    }
    
    // Save show-more expanded states before filter update
    const savedShowMoreStates = this.#saveShowMoreStates();
    if (savedShowMoreStates && savedShowMoreStates.size > 0) {
      window._savedShowMoreStates = savedShowMoreStates;
    } else {
      window._savedShowMoreStates = null;
    }
    
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection(loaderElement, isDrawerLoader);
   // this.#cleanupSearchModifications();
  };

  /**
   * Updates the selected filters slider separately using AJAX
   * @param {string} sectionId - The section ID
   */
  async #updateSelectedFiltersSlider(sectionId) {
    try {
      const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false);
      const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const sectionSelector = `#shopify-section-${sectionId}`;
      const sectionElement = fragment.querySelector(sectionSelector);
      
      if (sectionElement) {
        const newSliderContainer = sectionElement.querySelector('[data-selected-filters-slider-container]');
        const existingSliderContainer = document.querySelector('[data-selected-filters-slider-container]');
        
        if (newSliderContainer && existingSliderContainer) {
          existingSliderContainer.innerHTML = newSliderContainer.innerHTML;
        } else if (newSliderContainer && !existingSliderContainer) {
          const resultsList = document.querySelector('results-list');
          if (resultsList && resultsList.parentNode) {
            resultsList.parentNode.insertBefore(newSliderContainer.cloneNode(true), resultsList);
          }
        } else if (!newSliderContainer && existingSliderContainer) {
          existingSliderContainer.innerHTML = '';
        }
      }
    } catch (error) {
      console.error('Error updating selected filters slider:', error);
    }
  }

  /**
   * Updates the section
   * @param {HTMLElement | null} [loaderElement] - Optional loader element reference
   * @param {boolean} [isDrawerLoader] - Whether drawer loader was used (filter drawer is open on mobile)
   */
  async #updateSection(loaderElement = null, isDrawerLoader = false) {

    try {
      await sectionRenderer.renderSection(this.sectionId);
      await this.#updateSelectedFiltersSlider(this.sectionId);
      this.#scrollToProductGrid();
    } catch (error) {
      console.error('Error updating section:', error);
    } finally {
      // Keep loader visible briefly to make loading state transitions smoother.
      setTimeout(() => {
        if (isDrawerLoader && typeof hideDrawerLoader === 'function') {
          hideDrawerLoader(loaderElement);
        } else if (typeof hideLoader === 'function') {
          hideLoader(loaderElement);
        }
      }, 300);
    }
  }

  /**
   * Gets the accurate header height accounting for sticky positioning
   * On mobile, gets .header-section which contains both header-component and search bar
   * @returns {number} The header height in pixels
   */
  #getHeaderHeight() {
    const isMobile = window.innerWidth < 750;
    
    if (isMobile) {
      // On mobile, .header-section contains both header-component and search bar (both fixed)
      const headerSection = document.querySelector('.header-section');
      if (headerSection instanceof HTMLElement) {
        return headerSection.offsetHeight;
      }
    } else {
      // On desktop, use CSS variable for performance
      const headerHeightVar = getComputedStyle(document.body).getPropertyValue('--header-height');
      if (headerHeightVar) {
        const height = parseFloat(headerHeightVar);
        if (!isNaN(height) && height > 0) {
          return height;
        }
      }
    }

    // Fallback: Calculate from header-component
    const header = document.querySelector('header-component');
    if (header instanceof HTMLElement) {
      return header.offsetHeight;
    }

    return 0;
  }

  /**
   * Scrolls to the top of the product grid section
   */
  #scrollToProductGrid() {
    // Wait for next frame to ensure DOM is fully updated
    requestAnimationFrame(() => {
      // Try multiple selectors to find the product grid section
      const productGridSection = 
        document.getElementById('ResultsList') ||
        document.querySelector('.product-grid-container') ||
        document.querySelector('results-list') ||
        document.querySelector('.product-grid');
      
      if (productGridSection instanceof HTMLElement) {
        // Calculate accurate header height
        const headerHeight = this.#getHeaderHeight();
        const elementPosition = productGridSection.getBoundingClientRect().top;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const offsetPosition = elementPosition + scrollTop - headerHeight;
        
        window.scrollTo({
          top: Math.max(0, offsetPosition), // Ensure we don't scroll to negative position
          behavior: 'smooth'
        });
      }
    });
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    const isMobile = window.innerWidth < 750;
    const isFilterDrawerOpen = this.#isFilterDrawerOpen();
    
    // Show drawer loader only if filter drawer is open on mobile
    // Otherwise use regular loader
    const loaderElement = (isMobile && isFilterDrawerOpen) ? showDrawerLoader() : showLoader();
    const isDrawerLoader = isMobile && isFilterDrawerOpen;

    const nextUrl = new URL(url, window.location.origin);
    const viewParam = new URL(window.location.href).searchParams.get("view");
    if (viewParam && !nextUrl.searchParams.get("view")) {
      nextUrl.searchParams.set("view", viewParam);
    }
    
    history.pushState("", "", nextUrl);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection(loaderElement, isDrawerLoader);
  }
}

if (!customElements.get("facets-form-component")) {
  customElements.define("facets-form-component", FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest(".shopify-section")?.id;
    if (!id)
      throw new Error("FacetInputs component must be a child of a section");
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   * @param {Event} event - The event that triggered the update
   * @returns {void}
   */
  updateFilters(event) {
    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;
    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector("input");

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest("form");
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector("input");

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked)
      formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters)
      url.searchParams.append(key, value);

    if (inputElement.checked)
      url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter(
      (input) => input.checked
    );
    const details = this.closest("details");
    const statusComponent = details?.querySelector("facet-status-component");

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get("facet-inputs-component")) {
  customElements.define("facet-inputs-component", FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 * @property {HTMLInputElement} minSlider - The minimum price slider
 * @property {HTMLInputElement} maxSlider - The maximum price slider
 * @property {HTMLElement} sliderLabels - The slider labels container
 * @property {HTMLElement} sliderRange - The slider range indicator
 * @property {HTMLElement} sliderDots - The slider dots container
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.#initializeSliders();
    this.#setupMutationObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#removeSliderListeners();
    this.#disconnectMutationObserver();
  }

  /**
   * Called when the component is re-rendered by the Section Rendering API (morph).
   * Reinitializes sliders and regenerates labels/dots after content update.
   */
  updatedCallback() {
    super.updatedCallback();
    // Reinitialize sliders after morph updates the DOM
    this.#initializeSliders();
  }

  /**
   * Sets up MutationObserver to detect DOM changes and reinitialize sliders
   */
  #setupMutationObserver() {
    // Observe changes to the labels container
    const labelsContainer = this.querySelector('.price-facet__slider-labels');
    if (!labelsContainer) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      // Check if labels were removed (childList mutation with removed nodes)
      const labelsRemoved = mutations.some(mutation => 
        mutation.type === 'childList' && 
        mutation.removedNodes.length > 0 &&
        Array.from(mutation.removedNodes).some(node => 
          node instanceof Element && 
          node.classList && 
          node.classList.contains('price-facet__slider-label')
        )
      );

      if (labelsRemoved) {
        // Wait for the next animation frame to ensure DOM is fully updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.#initializeSliders();
          });
        });
      }
    });

    // Start observing
    this.mutationObserver.observe(labelsContainer, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Disconnects the MutationObserver
   */
  #disconnectMutationObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  /**
   * Initializes the range sliders and sets up event listeners
   */
  #initializeSliders() {
    // Get slider elements
    const minSlider = this.#getElement('minSlider', 'input.price-facet__slider--min');
    const maxSlider = this.#getElement('maxSlider', 'input.price-facet__slider--max');
    
    // Get input elements consistently
    const inputs = this.querySelectorAll('.price-facet__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    
    const sliderLabels = this.#getElement('sliderLabels', '.price-facet__slider-labels');

    if (!(minSlider instanceof HTMLInputElement) || !(maxSlider instanceof HTMLInputElement)) {
      return;
    }

    // Generate price labels (this will also generate dots internally)
    if (sliderLabels instanceof HTMLElement) {
      this.#generatePriceLabels(sliderLabels);
    }

    // Initialize slider positions based on input values
    this.#updateSliderFromInputs();

    // Add event listeners for sliders
    // 'input' event for visual updates while dragging
    minSlider.addEventListener('input', this.#handleMinSliderChange);
    maxSlider.addEventListener('input', this.#handleMaxSliderChange);
    // 'change' event for filter update only when user releases (mouseup/touchend)
    minSlider.addEventListener('change', this.#handleMinSliderRelease);
    maxSlider.addEventListener('change', this.#handleMaxSliderRelease);

    // Add event listeners for inputs to update sliders
    if (minInput instanceof HTMLInputElement) {
      minInput.addEventListener('input', this.#handleMinInputChange);
    }
    if (maxInput instanceof HTMLInputElement) {
      maxInput.addEventListener('input', this.#handleMaxInputChange);
    }
  }

  /**
   * Gets an element from refs or by selector
   * @param {string} refName - The ref name
   * @param {string} selector - The CSS selector fallback
   * @returns {Element | null}
   */
  #getElement(refName, selector) {
    const fromRefs = this.refs[refName];
    if (fromRefs instanceof Element) return fromRefs;
    if (Array.isArray(fromRefs) && fromRefs[0] instanceof Element) return fromRefs[0];
    return this.querySelector(`[ref="${refName}"]`) || this.querySelector(selector);
  }

  /**
   * Removes slider event listeners
   */
  #removeSliderListeners() {
    const minSlider = this.#getElement('minSlider', 'input.price-facet__slider--min');
    const maxSlider = this.#getElement('maxSlider', 'input.price-facet__slider--max');
    
    // Get input elements consistently
    const inputs = this.querySelectorAll('.price-facet__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (minSlider instanceof HTMLInputElement) {
      minSlider.removeEventListener('input', this.#handleMinSliderChange);
      minSlider.removeEventListener('change', this.#handleMinSliderRelease);
    }
    if (maxSlider instanceof HTMLInputElement) {
      maxSlider.removeEventListener('input', this.#handleMaxSliderChange);
      maxSlider.removeEventListener('change', this.#handleMaxSliderRelease);
    }
    if (minInput instanceof HTMLInputElement) {
      minInput.removeEventListener('input', this.#handleMinInputChange);
    }
    if (maxInput instanceof HTMLInputElement) {
      maxInput.removeEventListener('input', this.#handleMaxInputChange);
    }
  }

  /**
   * Generates price labels below the slider and dots on the slider
   * Divides range into 4 equal parts for 5 labels: 0, 25%, 50%, 75%, max
   * Values are rounded to nearest suitable number (10, 100, 1000, etc.)
   * @param {HTMLElement} container - The container element for labels
   */
  #generatePriceLabels(container) {
    // Parse maxPrice - remove commas and other non-numeric characters
    const maxPriceRaw = container.dataset.maxPrice || '0';
    const maxPrice = parseFloat(maxPriceRaw.replace(/[^0-9.-]/g, ''));
    const currencySymbol = container.dataset.currencySymbol || '$';

    if (maxPrice === 0 || isNaN(maxPrice)) {
      return;
    }

    // Calculate 5 evenly distributed values: 0, 25%, 50%, 75%, 100%
    const labels = this.#calculateStepValues(maxPrice);

    // Clear existing labels
    container.innerHTML = '';

    // Create label elements
    labels.forEach((value, index) => {
      const label = document.createElement('span');
      label.className = 'price-facet__slider-label';
      
      // Add position-specific class
      if (index === 0) {
        label.classList.add('price-facet__slider-label--first');
      } else if (index === labels.length - 1) {
        label.classList.add('price-facet__slider-label--last');
      } else {
        label.classList.add('price-facet__slider-label--middle');
        // For middle labels, set left position
        const position = (value / maxPrice) * 100;
        label.style.left = `${position}%`;
      }
      
      // Format the price
      const formattedValue = this.#formatPrice(value, currencySymbol);
      label.textContent = formattedValue;

      container.appendChild(label);
    });

    // Generate dots on the slider
    this.#generateSliderDots(labels, maxPrice);
  }

  /**
   * Calculates step values for the price range
   * Returns 5 values: 0, step1, step2, step3, max
   * Middle steps are rounded to nearest appropriate value (10, 100, 1000)
   * @param {number} maxPrice - Maximum price value
   * @returns {number[]} Array of 5 price values
   */
  #calculateStepValues(maxPrice) {
    // Determine rounding factor based on max price magnitude
    let roundTo;
    if (maxPrice < 100) {
      roundTo = 5; // Round to nearest 5
    } else if (maxPrice < 1000) {
      roundTo = 10; // Round to nearest 10
    } else if (maxPrice < 10000) {
      roundTo = 100; // Round to nearest 100
    } else if (maxPrice < 100000) {
      roundTo = 1000; // Round to nearest 1000
    } else {
      roundTo = 10000; // Round to nearest 10000
    }

    // Calculate 4 intervals (for 5 labels)
    const rawStep = maxPrice / 4;
    
    // Round each step value
    const step1 = Math.round(rawStep / roundTo) * roundTo;
    const step2 = Math.round((rawStep * 2) / roundTo) * roundTo;
    const step3 = Math.round((rawStep * 3) / roundTo) * roundTo;

    // Ensure steps are valid (not 0 unless max is very small)
    const labels = [
      0,
      step1 || Math.round(maxPrice * 0.25),
      step2 || Math.round(maxPrice * 0.5),
      step3 || Math.round(maxPrice * 0.75),
      maxPrice
    ];

    return labels;
  }

  /**
   * Generates dots on the slider track
   * @param {number[]} values - Array of price values for dot positions
   * @param {number} maxPrice - Maximum price value
   */
  #generateSliderDots(values, maxPrice) {
    // Find sliderDots element directly since refs might not have it
    const sliderDots = this.querySelector('[ref="sliderDots"]') || this.querySelector('.price-facet__slider-dots');
    
    if (!(sliderDots instanceof HTMLElement)) {
      return;
    }

    // Store values for later color updates
    this.dotValues = values;
    this.dotMaxPrice = maxPrice;

    // Clear existing dots
    sliderDots.innerHTML = '';

    // Create dot elements
    values.forEach((value, index) => {
      const dot = document.createElement('span');
      dot.className = 'price-facet__slider-dot';
      
      // Add special classes for first and last dots (larger size)
      if (index === 0) {
        dot.classList.add('price-facet__slider-dot--first');
      } else if (index === values.length - 1) {
        dot.classList.add('price-facet__slider-dot--last');
      }

      // Store the value as data attribute for color updates
      dot.dataset.dotValue = value.toString();

      // Position the dot
      const position = (value / maxPrice) * 100;
      dot.style.left = `${position}%`;

      sliderDots.appendChild(dot);
    });

    // Initialize dot colors based on current slider values
    const minSlider = this.#getElement('minSlider', 'input.price-facet__slider--min');
    const maxSlider = this.#getElement('maxSlider', 'input.price-facet__slider--max');
    if (minSlider instanceof HTMLInputElement && maxSlider instanceof HTMLInputElement) {
      const minValue = parseFloat(minSlider.value);
      const maxValue = parseFloat(maxSlider.value);
      const min = parseFloat(minSlider.min);
      const max = parseFloat(maxSlider.max);
      this.#updateDotColors(minValue, maxValue, min, max);
    }
  }

  /**
   * Updates dot colors based on current selection
   * Dots within the selected range are purple, dots outside are grey
   * When no filter is applied, all dots are grey
   * @param {number} selectedMin - Currently selected minimum value
   * @param {number} selectedMax - Currently selected maximum value
   * @param {number} absoluteMin - Absolute minimum value (0)
   * @param {number} absoluteMax - Absolute maximum value
   */
  #updateDotColors(selectedMin, selectedMax, absoluteMin, absoluteMax) {
    if (!this.dotValues || !this.dotMaxPrice) return;

    const sliderDots = this.querySelector('[ref="sliderDots"]') || this.querySelector('.price-facet__slider-dots');
    if (!(sliderDots instanceof HTMLElement)) return;

    // Check if filter is actually applied by checking input fields
    const inputs = this.querySelectorAll('.price-facet__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    const inputsEmpty = (!minInput || !(minInput instanceof HTMLInputElement) || !minInput.value.trim()) && 
                        (!maxInput || !(maxInput instanceof HTMLInputElement) || !maxInput.value.trim());
    
    // Check if sliders are at default positions (full range selected = no filter)
    const epsilon = 0.01;
    const isAtDefaultPosition = (selectedMin <= absoluteMin + epsilon) && (selectedMax >= absoluteMax - epsilon);
    
    // No filter is selected if inputs are empty OR sliders are at default positions
    const isNoFilterSelected = inputsEmpty || isAtDefaultPosition;

    const dots = sliderDots.querySelectorAll('.price-facet__slider-dot');

    dots.forEach((dot) => {
      if (!(dot instanceof HTMLElement)) return;
      
      const dotValue = parseFloat(dot.dataset.dotValue || '0');
      if (isNaN(dotValue)) return;
      
      // Check if this is first or last dot
      const isFirst = dot.classList.contains('price-facet__slider-dot--first');
      const isLast = dot.classList.contains('price-facet__slider-dot--last');

      // If no filter is applied, make all dots grey (including first and last)
      if (isNoFilterSelected) {
        dot.style.background = '#888888';
        if (isFirst || isLast) {
          dot.style.width = '14px';
          dot.style.height = '14px';
        } else {
          dot.style.width = '8px';
          dot.style.height = '8px';
        }
        return;
      }

      // Check if dot is within the selected range
      const isInSelectedRange = dotValue >= selectedMin - epsilon && dotValue <= selectedMax + epsilon;

      // Determine color: purple if in selected range, grey if outside
      // All dots (first, last, and middle) are same size (8px) for consistent centering
      if (isInSelectedRange) {
        // Dot is within selected range - make it purple
        dot.style.background = '#787FDE';
        // All dots same size for proper centering
        dot.style.width = '8px';
        dot.style.height = '8px';
      } else {
        // Dot is outside selected range - make it grey
        dot.style.background = '#888888';
        // All dots same size for proper centering
        dot.style.width = '8px';
        dot.style.height = '8px';
      }
    });
  }

  /**
   * Formats a price value with currency symbol
   * @param {number} value - The price value
   * @param {string} symbol - The currency symbol
   * @returns {string} Formatted price string
   */
  #formatPrice(value, symbol) {
    // Format with appropriate decimal places
    if (value >= 1000) {
      // For thousands, always show as integer (e.g., 13K, not 13.0K)
      const thousands = value / 1000;
      return `${symbol}${Math.round(thousands)}K`;
    } else if (value % 1 === 0) {
      // Whole number
      return `${symbol}${Math.round(value)}`;
    } else if (value >= 10) {
      // For values >= 10, show 1 decimal place if needed
      return `${symbol}${value.toFixed(1).replace(/\.0$/, '')}`;
    } else {
      // For small values, show 2 decimal places
      return `${symbol}${value.toFixed(2).replace(/\.00$/, '')}`;
    }
  }

  /**
   * Handles min slider change (visual updates only while dragging)
   */
  #handleMinSliderChange = () => {
    const minSlider = this.#getElement('minSlider', 'input.price-facet__slider--min');
    const maxSlider = this.#getElement('maxSlider', 'input.price-facet__slider--max');
    
    // Get min input consistently
    const inputs = this.querySelectorAll('.price-facet__input');
    const minInput = inputs[0];

    if (!(minSlider instanceof HTMLInputElement) || !(maxSlider instanceof HTMLInputElement)) {
      return;
    }

    const minValue = parseFloat(minSlider.value);
    const maxValue = parseFloat(maxSlider.value);

    // Ensure min slider doesn't exceed max slider
    if (minValue > maxValue) {
      minSlider.value = maxValue.toString();
    }

    // Update min input
    if (minInput instanceof HTMLInputElement) {
      minInput.value = minSlider.value;
    }

    // Only update visual range, don't trigger filter update
    this.#updateSliderRange();
  };

  /**
   * Handles min slider release (triggers filter update)
   */
  #handleMinSliderRelease = () => {
    this.#triggerFilterUpdate();
  };

  /**
   * Handles max slider change (visual updates only while dragging)
   */
  #handleMaxSliderChange = () => {
    const minSlider = this.#getElement('minSlider', 'input.price-facet__slider--min');
    const maxSlider = this.#getElement('maxSlider', 'input.price-facet__slider--max');
    
    // Get max input consistently
    const inputs = this.querySelectorAll('.price-facet__input');
    const maxInput = inputs[1];

    if (!(minSlider instanceof HTMLInputElement) || !(maxSlider instanceof HTMLInputElement)) {
      return;
    }

    const minValue = parseFloat(minSlider.value);
    const maxValue = parseFloat(maxSlider.value);

    // Ensure max slider doesn't go below min slider
    if (maxValue < minValue) {
      maxSlider.value = minValue.toString();
    }

    // Update max input
    if (maxInput instanceof HTMLInputElement) {
      maxInput.value = maxSlider.value;
    }

    // Only update visual range, don't trigger filter update
    this.#updateSliderRange();
  };

  /**
   * Handles max slider release (triggers filter update)
   */
  #handleMaxSliderRelease = () => {
    this.#triggerFilterUpdate();
  };

  /**
   * Handles min input change to update slider
   */
  #handleMinInputChange = () => {
    this.#updateSliderFromInputs();
  };

  /**
   * Handles max input change to update slider
   */
  #handleMaxInputChange = () => {
    this.#updateSliderFromInputs();
  };

  /**
   * Updates slider positions from input values
   */
  #updateSliderFromInputs() {
    const minSlider = this.#getElement('minSlider', 'input.price-facet__slider--min');
    const maxSlider = this.#getElement('maxSlider', 'input.price-facet__slider--max');
    
    // Get input elements - use consistent selectors
    const inputs = this.querySelectorAll('.price-facet__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (!(minSlider instanceof HTMLInputElement) || !(maxSlider instanceof HTMLInputElement)) {
      return;
    }

    // Update min slider
    if (minInput instanceof HTMLInputElement && minInput.value) {
      const minValue = parseFloat(minInput.value.replace(/[^0-9.-]/g, ''));
      if (!isNaN(minValue)) {
        minSlider.value = minValue.toString();
      }
    } else {
      minSlider.value = minSlider.min;
    }

    // Update max slider
    if (maxInput instanceof HTMLInputElement && maxInput.value) {
      const maxValue = parseFloat(maxInput.value.replace(/[^0-9.-]/g, ''));
      if (!isNaN(maxValue)) {
        maxSlider.value = maxValue.toString();
      }
    } else {
      maxSlider.value = maxSlider.max;
    }

    this.#updateSliderRange();
  }

  /**
   * Updates the visual range between sliders
   * When no filter is selected (inputs are empty), the range is hidden (grey bar)
   * When a filter is selected, the range shows in purple
   */
  #updateSliderRange() {
    const minSlider = this.#getElement('minSlider', 'input.price-facet__slider--min');
    const maxSlider = this.#getElement('maxSlider', 'input.price-facet__slider--max');
    const sliderRange = this.querySelector('[ref="sliderRange"]') || this.querySelector('.price-facet__slider-range');
    
    // Get input elements to check if filter is actually selected
    const inputs = this.querySelectorAll('.price-facet__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (!(minSlider instanceof HTMLInputElement) || !(maxSlider instanceof HTMLInputElement)) {
      return;
    }

    if (!(sliderRange instanceof HTMLElement)) {
      return;
    }

    const minValue = parseFloat(minSlider.value);
    const maxValue = parseFloat(maxSlider.value);
    const min = parseFloat(minSlider.min);
    const max = parseFloat(minSlider.max);

    // Check if no filter is selected by checking if both input fields are empty
    const isNoFilterSelected = (!minInput || !(minInput instanceof HTMLInputElement) || !minInput.value.trim()) && 
                                (!maxInput || !(maxInput instanceof HTMLInputElement) || !maxInput.value.trim());

    if (isNoFilterSelected) {
      // Hide the purple range - show grey bar only
      sliderRange.style.left = '0';
      sliderRange.style.width = '0';
    } else {
      // Show the selected range in purple
      const minPercent = ((minValue - min) / (max - min)) * 100;
      const maxPercent = ((maxValue - min) / (max - min)) * 100;

      sliderRange.style.left = `${minPercent}%`;
      sliderRange.style.width = `${maxPercent - minPercent}%`;
    }

    // Update dot colors based on current selection
    this.#updateDotColors(minValue, maxValue, min, max);
  }

  /**
   * Triggers filter update with debounce
   */
  #triggerFilterUpdate = debounce(() => {
    this.updatePriceFilterAndResults();
  }, 300);

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    // Get input elements - use consistent selectors
    const inputs = this.querySelectorAll('.price-facet__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) {
      return;
    }

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (!(input instanceof HTMLInputElement)) return;
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute("data-min") ?? ""));
    const max = Number(formatMoney(input.getAttribute("data-max") ?? ""));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    // Get input elements - use consistent selectors
    const inputs = this.querySelectorAll('.price-facet__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) {
      return;
    }

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', minInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    // Get input elements - use consistent selectors
    const inputs = this.querySelectorAll('.price-facet__input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (!(minInput instanceof HTMLInputElement) || !(maxInput instanceof HTMLInputElement)) {
      return;
    }

    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get("price-facet-component")) {
  customElements.define("price-facet-component", PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ["clearButton"];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("keyup", this.#handleKeyUp);
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest(
      "facet-inputs-component, price-facet-component"
    );
    container
      ?.querySelectorAll('[type="checkbox"]:checked, input')
      .forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.checked = false;
          input.value = "";
        }
      });

    const details = event.target.closest("details");
    const statusComponent = details?.querySelector("facet-status-component");

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest("facets-form-component");
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // updateFilters() already shows the loader, so just call it
    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === "Enter") this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle(
        "facets__clear--active",
        event.shouldShowClearAll()
      );
    }
  };
}

if (!customElements.get("facet-clear-component")) {
  customElements.define("facet-clear-component", FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    // Try to find facets-form-component:
    // 1. By form ID if provided
    // 2. By closest() if inside the component
    // 3. By section-id attribute (for components outside the form, like horizontal slider)
    let facetsForm = form
      ? document.getElementById(form)
      : this.closest("facets-form-component");

    // Fallback: find by section-id from data-section-id attribute
    if (!facetsForm && this.dataset.sectionId) {
      const section = document.getElementById(`shopify-section-${this.dataset.sectionId}`);
      if (section) {
        facetsForm = section.querySelector("facets-form-component");
      }
    }

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // updateFiltersByURL() already shows the loader, so just call it
    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle("active", event.shouldShowClearAll());
    }
  };
}

if (!customElements.get("facet-remove-component")) {
  customElements.define("facet-remove-component", FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ["details", "summary", "listbox"];

  connectedCallback() {
    super.connectedCallback();

    // Listen for change events on radio inputs
    this.addEventListener("change", (event) => {
      if (
        event.target instanceof HTMLInputElement &&
        event.target.type === "radio" &&
        event.target.name === "sort_by"
      ) {
        this.updateSortStatusDisplay();
      }
    });
  }

  /**
   * Updates the sort status display by finding the checked radio
   */
  updateSortStatusDisplay() {
    // Find the checked radio input
    const checkedRadio = this.querySelector(
      'input[type="radio"][name="sort_by"]:checked'
    );
    if (!checkedRadio) return;

    // Get the option name directly from the label span
    const optionElement = checkedRadio.closest('[role="option"]');
    const labelSpan = optionElement
      ? optionElement.querySelector(".sorting-filter__label")
      : null;
    const optionName = labelSpan ? labelSpan.textContent.trim() : "";

    // Update the sort status span
    const sortStatusSpan = this.querySelector("[data-sort-status]");
    if (sortStatusSpan instanceof HTMLElement && optionName) {
      sortStatusSpan.textContent = optionName;
    }
  }

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find(
      (option) => option instanceof HTMLElement && option.tabIndex === 0
    );
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case "ArrowUp":
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case "Enter":
      case " ":
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case "Escape":
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (
      !(details instanceof HTMLDetailsElement) ||
      !(summary instanceof HTMLElement)
    )
      return;

    const isOpen = details.open;
    summary.setAttribute("aria-expanded", isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute("aria-selected", "false");
      });
      option.setAttribute("aria-selected", "true");

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest("facets-form-component") ||
      this.closest(".shopify-section")?.querySelector("facets-form-component");

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === "true";

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    // Handle mobile select element
    if (event.target instanceof HTMLSelectElement) {
      const optionName =
        event.target.options[event.target.selectedIndex]?.text ?? "";
      const sortStatusSpan = this.querySelector("[data-sort-status]");
      if (sortStatusSpan instanceof HTMLElement) {
        sortStatusSpan.textContent = optionName;
      }
      const details = this.querySelector("details");
      const defaultSortBy =
        details instanceof HTMLDetailsElement
          ? details.dataset.defaultSortBy
          : undefined;
      this.classList.toggle(
        "sorting-filter--active",
        Boolean(defaultSortBy) && event.target.value !== defaultSortBy
      );
      return;
    }

    // Update facet-status-component if it exists
    const details = this.querySelector("details");
    if (details) {
      const checkedRadio = this.querySelector(
        'input[type="radio"][name="sort_by"]:checked'
      );
      if (checkedRadio instanceof HTMLInputElement) {
        const isActive = checkedRadio.value !== details.dataset.defaultSortBy;
        this.classList.toggle("sorting-filter--active", isActive);
        const facetStatus = details.querySelector("facet-status-component");
        if (facetStatus instanceof FacetStatusComponent) {
          const optionElement = checkedRadio.closest('[role="option"]');
          const labelSpan = optionElement
            ? optionElement.querySelector(".sorting-filter__label")
            : null;
          const optionName = labelSpan ? labelSpan.textContent.trim() : "";
          facetStatus.textContent = isActive ? optionName : "";
        }
      }
    }
  }
}

if (!customElements.get("sorting-filter-component")) {
  customElements.define("sorting-filter-component", SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute("facet-type") === "swatches"
      ? this.#updateSwatchSummary(
          checkedInputElements,
          checkedInputElementsCount
        )
      : this.#updateBubbleSummary(
          checkedInputElements,
          checkedInputElementsCount
        );
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    if (this.dataset.filterStyle === "horizontal_calmi") {
      this.#updateBubbleSummary(
        checkedInputElements,
        checkedInputElementsCount
      );
      return;
    }

    const { facetStatus } = this.refs;
    facetStatus.classList.remove("bubble", "facets__bubble");

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = "";
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add("bubble", "facets__bubble");
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector("span.swatch");
        return swatch?.outerHTML ?? "";
      })
      .join("");
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove("bubble", "facets__bubble");

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = "";
      return;
    }

    if (filterStyle === "horizontal" && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = `(${escapeHtml(checkedInputElements[0]?.dataset.label ?? "")})`;
      return;
    }

    facetStatus.innerHTML = `(${checkedInputElementsCount.toString()})`;
    if (filterStyle !== "horizontal_calmi") {
      facetStatus.classList.add("bubble", "facets__bubble");
    }
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = "";
      return;
    }

    if (this.dataset.filterStyle === "horizontal_calmi") {
      facetStatus.innerHTML = "(1)";
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, "0");
    const maxInputNum = this.#parseCents(
      maxInputValue,
      facetStatus.dataset.rangeMax
    );
    facetStatus.innerHTML = `${this.#formatMoney(
      minInputNum
    )}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = "0") {
    const parts = value
      ? value.trim().split(/[^0-9]/)
      : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== "string" || rest.length > 0)
      return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || "0", 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return "";

    const template = this.refs.moneyFormat.content.textContent || "{{amount}}";
    const currency = this.refs.facetStatus.dataset.currency || "";

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== "string") return "";
      if (placeholder === "currency") return currency;

      let thousandsSeparator = ",";
      let decimalSeparator = ".";
      let precision =
        CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === "amount") {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === "amount_no_decimals") {
        precision = 0;
      } else if (placeholder === "amount_with_comma_separator") {
        thousandsSeparator = ".";
        decimalSeparator = ",";
      } else if (placeholder === "amount_no_decimals_with_comma_separator") {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = ".";
        precision = 0;
      } else if (placeholder === "amount_no_decimals_with_space_separator") {
        thousandsSeparator = " ";
        precision = 0;
      } else if (placeholder === "amount_with_space_separator") {
        thousandsSeparator = " ";
        decimalSeparator = ",";
      } else if (placeholder === "amount_with_period_and_space_separator") {
        thousandsSeparator = " ";
        decimalSeparator = ".";
      } else if (placeholder === "amount_with_apostrophe_separator") {
        thousandsSeparator = "'";
        decimalSeparator = ".";
      }

      return this.#formatCents(
        moneyValue,
        thousandsSeparator,
        decimalSeparator,
        precision
      );
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split(".");
    if (!a) a = "0";
    if (!b) b = "";

    // Split by groups of 3 digits
    a = a.replace(
      /\d(?=(\d\d\d)+(?!\d))/g,
      (digit) => digit + thousandsSeparator
    );

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, "0");
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = "";
  }
}

if (!customElements.get("facet-status-component")) {
  customElements.define("facet-status-component", FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};
